import { useCallback } from 'react';
import { fileSystemService } from '../services/filesystem/FileSystemService';
import { useFileSystemStore } from '../store/useFileSystemStore';
import { fileSystemPersistence } from '../services/shared/fileSystemPersistence';
import { useProjectStore } from '../store/useProjectStore';
import type { TranslationFile } from '../types';

/**
 * US-19-16: lazy-load boundary for content-heavy file fields.
 *
 * Call BEFORE any code path that reads file.sourceText, file.aiOutput,
 * file.originalFile, file.docxParagraphMap, file.auditResult, file.semanticMatchResult.
 *
 * - contentLoaded=true → return immediately (cached)
 * - contentLoaded=false → read .source.txt/.translation.txt/.original.docx/etc from disk,
 *   merge into store, mark contentLoaded=true, return updated file.
 *
 * Module-level helper (no React hook deps) so it can be called from workflow hooks + actions.
 */
export async function ensureFileContentLoaded(fileId: string, force = false): Promise<TranslationFile | null> {
    const { files, updateFile } = useProjectStore.getState();
    const file = files.find(f => f.id === fileId);
    if (!file) return null;

    if (file.contentLoaded && !force) return file;

    const { rootHandle, isConnected } = useFileSystemStore.getState();
    if (!isConnected || !rootHandle) {
        // Disk not connected — return as-is, caller handles missing content
        return file;
    }

    try {
        (fileSystemPersistence as any).handle = rootHandle;
        const fullFile = await fileSystemPersistence.loadFileFull(file.projectId, fileId);
        if (!fullFile) {
            // File not found on disk — mark loaded to prevent retry loop, caller fallback
            updateFile(fileId, { contentLoaded: true });
            return useProjectStore.getState().files.find(f => f.id === fileId) || file;
        }

        // Auto-reconcile: nếu translation.txt có content nhưng state.json says error
        // → state stale, fix lên 'done' khi load. Bug class: client polling race vs server execution.
        const diskAiOutput = fullFile.aiOutput;
        const stateError = file.translationStatus === 'error';
        const diskHasContent = Array.isArray(diskAiOutput) && diskAiOutput.some(s => typeof s === 'string' && s.trim().length > 0);
        const reconcileToDone = stateError && diskHasContent;

        // Recovery US-19-16 follow-up: nếu disk thiếu sourceText nhưng có backendSegments
        // (Worker parser output đã persist) → reconstruct sourceText. Tránh "Tài liệu chưa có
        // nội dung" toast khi user click Dịch lại trên file translated trước fix US-19-16.
        const backendSegs = (fullFile as any).backendSegments ?? (file as any).backendSegments;
        const reconstructedSourceText =
            (fullFile.sourceText && fullFile.sourceText.length > 0)
                ? fullFile.sourceText
                : (Array.isArray(backendSegs) && backendSegs.length > 0)
                    ? backendSegs.join('\n')
                    : file.sourceText;

        updateFile(fileId, {
            sourceText: reconstructedSourceText,
            aiOutput: fullFile.aiOutput ?? file.aiOutput,
            originalFile: fullFile.originalFile ?? file.originalFile,
            docxParagraphMap: fullFile.docxParagraphMap ?? file.docxParagraphMap,
            auditResult: fullFile.auditResult ?? file.auditResult,
            auditRawOutput: fullFile.auditRawOutput ?? file.auditRawOutput,
            semanticMatchResult: fullFile.semanticMatchResult ?? file.semanticMatchResult,
            // Cache pre-flight credits — fix "đếm lại mỗi lần" khi syncFromDisk không hydrate field này
            estimatedCredits: (fullFile as any).estimatedCredits ?? file.estimatedCredits,
            contentLoaded: true,
            ...(reconcileToDone ? { translationStatus: 'done' as const, error: undefined } : {}),
        });

        if (reconcileToDone) {
            console.log(`[ensureFileContentLoaded] Auto-reconciled "${file.fileName}" — translation.txt has ${diskAiOutput.length} segments but state was 'error'. Fixed to 'done'.`);
        }

        return useProjectStore.getState().files.find(f => f.id === fileId) || null;
    } catch (e) {
        console.warn(`[ensureFileContentLoaded] Failed to load file ${fileId}:`, e);
        return file;
    }
}

export function useFileSystem() {
    const {
        isConnected,
        rootHandle,
        folderName,
        lastError,
        needsPermission,
        setConnected,
        setDisconnected,
        setLastError,
        initialize,
        syncFromDisk,
        reGrantPermission
    } = useFileSystemStore();

    const connect = useCallback(async () => {
        try {
            const handle = await fileSystemService.requestAccess();
            const hasPermission = await fileSystemService.verifyPermission(handle, 'readwrite');

            if (hasPermission) {
                // Sync handle to both stores
                setConnected(handle);
                (fileSystemPersistence as any).handle = handle;
                fileSystemPersistence.cleanupLegacyLogs();

                // US-19-16 Stage 1 round 2 concern #2: two-phase connect.
                // Set isSyncingFromDisk=true, schedule sync after first paint,
                // return immediately so UI renders with folder name + skeleton.
                useFileSystemStore.setState({ isSyncingFromDisk: true });
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        syncFromDisk().finally(() => {
                            useFileSystemStore.setState({ isSyncingFromDisk: false });
                        });
                    }, 0);
                });

                return handle;
            } else {
                throw new Error('Permission denied');
            }
        } catch (error: any) {
            setLastError(error.message || 'Failed to connect');
            throw error;
        }
    }, [setConnected, setLastError, syncFromDisk]);

    const disconnect = useCallback(() => {
        setDisconnected();
    }, [setDisconnected]);

    const readFile = useCallback(async (path: string) => {
        if (!rootHandle) throw new Error('Not connected');
        return await fileSystemService.readFile(rootHandle, path);
    }, [rootHandle]);

    const writeFile = useCallback(async (path: string, content: string) => {
        if (!rootHandle) throw new Error('Not connected');
        return await fileSystemService.writeFile(rootHandle, path, content);
    }, [rootHandle]);

    const listDirectory = useCallback(async () => {
        if (!rootHandle) throw new Error('Not connected');
        return await fileSystemService.listDirectory(rootHandle);
    }, [rootHandle]);

    return {
        isConnected,
        rootHandle,
        folderName,
        lastError,
        needsPermission,
        connect,
        disconnect,
        readFile,
        writeFile,
        listDirectory,
        initialize,
        syncFromDisk,
        reGrantPermission
    };
}
